import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";
import type { ConfidenceRating } from "./confidenceEngine";
import type { ContrarianAlert } from "./contrarianEngine";
import type { HeatWindow } from "./heatWindowEngine";
import type { StoryArcFrame } from "./storyArcEngine";
import type { CommunityInsight } from "./communitySentiment";
import type { ViralStatCard } from "@/lib/derivedIntel/viralPredictionCards";
import {
  pickDominantContrarian,
  type ContrarianPriority,
} from "./contrarianOrchestrator";

/**
 * EmotionalIntensityCoordinator — single arbiter that decides which
 * emotional event "wins" the current second-screen moment. The goal is
 * to avoid simultaneous noise (heat + contrarian + crowd + viral card
 * all blasting at once) and surface the DOMINANT signal.
 *
 * Pure function. Reads existing engines, writes nothing.
 */

export type DominantKind =
  | "story_arc"
  | "heat_window"
  | "contrarian"
  | "viral_card"
  | "crowd"
  | "idle";

export interface EmotionalSlot {
  kind: DominantKind;
  intensity: number; // 0..1
  headline: string;
  detail: string;
  sourceId?: string;
}

export interface EmotionalDirective {
  dominant: EmotionalSlot;
  /** Up to 2 secondary slots that can render quietly without competing. */
  secondary: EmotionalSlot[];
  /** Whether the UI should suppress non-essential notifications now. */
  suppressNoise: boolean;
}

export interface CoordinatorInputs {
  snap: DerivedSnapshot;
  rating: ConfidenceRating;
  arc?: StoryArcFrame;
  heat?: HeatWindow[];
  contrarian?: ContrarianAlert[];
  cards?: ViralStatCard[];
  insights?: CommunityInsight[];
}

const KIND_WEIGHT: Record<DominantKind, number> = {
  story_arc: 0.7,
  heat_window: 0.95,
  contrarian: 0.85,
  viral_card: 0.75,
  crowd: 0.6,
  idle: 0,
};

export function coordinateEmotionalIntensity(
  inputs: CoordinatorInputs,
): EmotionalDirective {
  const slots: EmotionalSlot[] = [];

  const contrarianTop: ContrarianPriority | null = inputs.contrarian
    ? pickDominantContrarian(inputs.contrarian, inputs.snap, inputs.rating.score)
    : null;
  if (contrarianTop) {
    slots.push({
      kind: "contrarian",
      intensity: contrarianTop.score,
      headline: contrarianTop.alert.headline,
      detail: contrarianTop.alert.detail,
      sourceId: contrarianTop.alert.id,
    });
  }

  const topHeat = inputs.heat?.find((h) => h.active) ?? inputs.heat?.[0];
  if (topHeat) {
    slots.push({
      kind: "heat_window",
      intensity: topHeat.intensity,
      headline: topHeat.label,
      detail: topHeat.detail,
      sourceId: topHeat.id,
    });
  }

  const topCard = inputs.cards?.[0];
  if (topCard) {
    slots.push({
      kind: "viral_card",
      intensity: topCard.virality,
      headline: topCard.headline,
      detail: topCard.subline,
      sourceId: topCard.id,
    });
  }

  if (inputs.arc) {
    slots.push({
      kind: "story_arc",
      intensity: inputs.arc.intensity,
      headline: inputs.arc.label,
      detail: inputs.arc.detail,
    });
  }

  const topInsight = inputs.insights?.[0];
  if (topInsight) {
    slots.push({
      kind: "crowd",
      intensity: topInsight.weight,
      headline: topInsight.text,
      detail: "Community sentiment",
      sourceId: topInsight.id,
    });
  }

  const scored = slots
    .map((s) => ({ slot: s, score: s.intensity * KIND_WEIGHT[s.kind] }))
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) {
    return {
      dominant: { kind: "idle", intensity: 0, headline: "", detail: "" },
      secondary: [],
      suppressNoise: false,
    };
  }
  const dominant = scored[0].slot;
  const secondary = scored.slice(1, 3).map((s) => s.slot);
  // Suppress noise when the dominant intensity is very high; everything
  // else should yield to it.
  const suppressNoise = dominant.intensity > 0.78;
  return { dominant, secondary, suppressNoise };
}